import cn from 'classnames';
import { memo, useMemo, type FC } from 'react';

import { generateKey } from '@app/utils';

import {
  clearAllHoverStyles,
  isActionsAllowed,
} from '@pages/WorkSchedule/utils';

import {
  WS_CONDITION_CHANGING,
  WS_CONDITION_DELETING,
  WS_CONDITION_HOVER_CONTAINER,
  WS_CONDITION_PRELOADED,
  WS_CONDITION_RECORD,
} from '@pages/WorkSchedule/constants/cellConditions';

import { TipPopover } from '@pages/WorkSchedule/components/TipPopover';

import type { NWorkSchedule } from '@pages/WorkSchedule/WorkSchedule.types';

export const ScheduleCell: FC<NWorkSchedule.ScheduleCell> = memo(
  ({
    cell,
    changingCell,
    deletingCell,
    employee,
    date,
    changeFewSchedulesCB,
    hoveredCells,
    mode,
    hoverSchedules,
    rights,
  }) => {
    const allowed = useMemo(
      () =>
        isActionsAllowed({
          date,
          schedule: !!cell?.schedule,
          mode,
          rights,
        }),
      [cell?.schedule, date, mode, rights]
    );

    const classNames = cn(
      'records cell ' + `employee-${employee.Id} date-${date}`,
      {
        [`schedule-${cell?.schedule?.Id}`]: !!cell?.schedule,
      },
      {
        [WS_CONDITION_PRELOADED]: !!cell?.schedule,
      },
      {
        [WS_CONDITION_CHANGING]: !!changingCell,
      },
      {
        [WS_CONDITION_DELETING]: !!cell?.schedule && !!deletingCell,
      },
      {
        delete: !!cell?.schedule && mode === 'delete',
      },
      {
        [WS_CONDITION_RECORD]: cell?.records || false,
      },
      {
        [WS_CONDITION_HOVER_CONTAINER]: !!cell?.schedule && allowed,
      }
    );

    return (
      <div
        data-test-id={`employee-${employee?.Id}-${date}`}
        key={generateKey('AddScheduleCell', employee.Id)}
        className={classNames}
        data-testid={
          cell?.schedule
            ? mode === 'delete'
              ? 'selected-value-to-delete'
              : 'busy-cell'
            : 'free-cell'
        }
        style={{
          pointerEvents: allowed ? 'auto' : 'none',
        }}
        onMouseOver={() => {
          hoverSchedules(employee, date);
        }}
        onMouseOut={() => {
          clearAllHoverStyles({
            hoveredCells,
          });
        }}
        onClick={() => {
          changeFewSchedulesCB(employee, date);
        }}
      >
        <div className={'time-range-container'} data-testid="time-to-add">
          {changingCell?.time || cell?.time}
        </div>
        {cell?.records ? (
          <div onClick={(event) => event.stopPropagation()}>
            <TipPopover employee={employee} date={date}>
              <div className={'record-container'}>
                <div className="record-item" data-testid="records-count">
                  {cell?.records}
                </div>
              </div>
            </TipPopover>
          </div>
        ) : null}
      </div>
    );
  }
);
